/**
 * Kambi OTC & Kiosk Balance Dashboard
 *
 * After authentication, shows:
 * 1. Grand total cash across all locations (hero metric)
 * 2. Per-shop summary cards with OTC + kiosk totals and health indicators
 * 3. Drill-down into a shop for per-terminal breakdown with progress bars
 */
import { Button, Field, Input } from '@apuestapr/ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import {
  createBalancesApi,
  type BalancesApi,
  type BalancesSummary,
  type KambiSessionStatus,
  type ShopBalanceSummary,
  type ShopBalances,
} from './api.ts';

// ─── Types ───────────────────────────────────────────────────────────────────

type PageState =
  | { readonly status: 'checking' }
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'authenticating' }
  | { readonly status: 'loading-summary' }
  | { readonly status: 'summary'; readonly summary: BalancesSummary }
  | {
      readonly status: 'loading-shop';
      readonly summary: BalancesSummary;
      readonly shopRef: string;
    }
  | {
      readonly status: 'shop-detail';
      readonly summary: BalancesSummary;
      readonly balances: ShopBalances;
    };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

/** Determine health color based on balance relative to deposit */
const getHealthColor = (balance: number | null, lastDeposit: number | null): string => {
  if (balance === null) return 'text-muted-foreground';
  if (lastDeposit === null || lastDeposit === 0) return 'text-foreground';
  const ratio = balance / lastDeposit;
  if (ratio > 0.6) return 'text-emerald-600';
  if (ratio > 0.3) return 'text-amber-500';
  return 'text-red-500';
};

const getHealthBg = (balance: number | null, lastDeposit: number | null): string => {
  if (balance === null) return 'bg-muted';
  if (lastDeposit === null || lastDeposit === 0) return 'bg-primary/10';
  const ratio = balance / lastDeposit;
  if (ratio > 0.6) return 'bg-emerald-50 border-emerald-200';
  if (ratio > 0.3) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
};

const getShopHealthColor = (shop: ShopBalanceSummary): string => {
  if (shop.totalCash <= 0) return 'border-red-300 bg-red-50';
  if (shop.otcWithBalance < shop.otcTerminalCount) return 'border-amber-300 bg-amber-50';
  return 'border-emerald-200 bg-emerald-50';
};

// ─── Login Form ──────────────────────────────────────────────────────────────

const KambiLoginForm = ({
  api,
  csrfToken,
  onSuccess,
}: {
  readonly api: BalancesApi;
  readonly csrfToken: string;
  readonly onSuccess: () => void;
}): ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    void api.authenticate(email, password, totpCode, csrfToken).then(
      () => {
        onSuccess();
      },
      (err: Error) => {
        setBusy(false);
        setError(err.message || 'La autenticacion fallo. Verifica tus credenciales y codigo TOTP.');
      },
    );
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-2xl border border-border bg-card p-8 shadow-overlay">
        <div className="mb-6 space-y-2 text-center">
          <h2 className="font-display text-2xl font-bold">Kambi Back Office</h2>
          <p className="text-sm text-muted-foreground">
            Ingresa tus credenciales de Kambi para ver los balances de OTC y kioscos.
          </p>
        </div>

        {error !== null ? (
          <p
            className="mb-4 rounded-xl border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <form className="space-y-4" noValidate onSubmit={submit}>
          <Field label="Email de Kambi" required>
            {(control) => (
              <Input
                {...control}
                type="email"
                autoComplete="email"
                placeholder="usuario@apuestapr.com"
                value={email}
                disabled={busy}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Contrasena" required>
            {(control) => (
              <Input
                {...control}
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Field label="Codigo TOTP (2FA)" required>
            {(control) => (
              <Input
                {...control}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={totpCode}
                disabled={busy}
                onChange={(e) => setTotpCode(e.target.value)}
              />
            )}
          </Field>
          <Button className="w-full" type="submit" loading={busy} loadingLabel="Autenticando...">
            Acceder a Balances
          </Button>
        </form>
      </div>
    </div>
  );
};

// ─── Grand Total Hero ────────────────────────────────────────────────────────

const GrandTotalHero = ({ summary }: { readonly summary: BalancesSummary }): ReactElement => (
  <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/30 p-8 shadow-overlay">
    <div className="grid gap-6 sm:grid-cols-3">
      {/* Grand Total */}
      <div className="sm:col-span-3 text-center">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Efectivo Total en Mano
        </p>
        <p className="mt-2 font-display text-5xl font-bold text-foreground">
          {formatCurrency(summary.totals.grandTotal)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {String(summary.totals.shopCount)} localidades &middot; Actualizado{' '}
          {new Date(summary.fetchedAt).toLocaleTimeString('es-PR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>

      {/* OTC Total */}
      <div className="text-center rounded-xl bg-card/60 p-4 border border-border/50">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          OTC (Cajas)
        </p>
        <p className="mt-1 font-display text-2xl font-bold text-emerald-600">
          {formatCurrency(summary.totals.otcTotal)}
        </p>
      </div>

      {/* Kiosk Total */}
      <div className="text-center rounded-xl bg-card/60 p-4 border border-border/50">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Kioscos (SST)
        </p>
        <p className="mt-1 font-display text-2xl font-bold text-blue-600">
          {formatCurrency(summary.totals.kioskTotal)}
        </p>
      </div>

      {/* Locations */}
      <div className="text-center rounded-xl bg-card/60 p-4 border border-border/50">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Localidades
        </p>
        <p className="mt-1 font-display text-2xl font-bold text-foreground">
          {String(summary.totals.shopCount)}
        </p>
      </div>
    </div>
  </div>
);

// ─── Shop Summary Cards ──────────────────────────────────────────────────────

const ShopSummaryGrid = ({
  shops,
  onSelect,
}: {
  readonly shops: readonly ShopBalanceSummary[];
  readonly onSelect: (shopRef: string) => void;
}): ReactElement => {
  // Sort: highest total first
  const sorted = useMemo(() => [...shops].sort((a, b) => b.totalCash - a.totalCash), [shops]);

  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold">Resumen por Localidad</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((shop) => (
          <button
            key={shop.shopRef}
            type="button"
            onClick={() => onSelect(shop.shopRef)}
            className={`rounded-xl border p-5 text-left transition-all duration-fast ease-standard hover:shadow-overlay hover:scale-[1.01] ${getShopHealthColor(shop)}`}
          >
            <div className="flex items-start justify-between">
              <h3 className="font-display font-bold text-sm leading-tight">{shop.shopName}</h3>
              <span className="shrink-0 ml-2 font-display text-lg font-bold">
                {formatCurrency(shop.totalCash)}
              </span>
            </div>

            {shop.address !== null ? (
              <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{shop.address}</p>
            ) : null}

            <div className="mt-3 flex gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">OTC: </span>
                <span className="font-semibold text-emerald-700">
                  {formatCurrency(shop.otcTotal)}
                </span>
              </div>
              {shop.kioskCount > 0 ? (
                <div>
                  <span className="text-muted-foreground">Kiosk: </span>
                  <span className="font-semibold text-blue-700">
                    {formatCurrency(shop.kioskTotal)}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span>
                {String(shop.otcWithBalance)}/{String(shop.otcTerminalCount)} terminales activos
              </span>
              {shop.kioskCount > 0 ? <span>&middot; {String(shop.kioskCount)} kioscos</span> : null}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Shop Detail View ────────────────────────────────────────────────────────

const ProgressBar = ({
  current,
  max,
}: {
  readonly current: number;
  readonly max: number;
}): ReactElement => {
  const pct = max > 0 ? Math.min(Math.max((current / max) * 100, 0), 100) : 0;
  const color = pct > 60 ? 'bg-emerald-500' : pct > 30 ? 'bg-amber-400' : 'bg-red-500';

  return (
    <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-300 ${color}`}
        style={{ width: `${String(pct)}%` }}
      />
    </div>
  );
};

const ShopDetailView = ({
  balances,
  onBack,
}: {
  readonly balances: ShopBalances;
  readonly onBack: () => void;
}): ReactElement => {
  const otcEntries = useMemo(() => {
    const entries = Object.entries(balances.otcBalances);
    // Sort by balance ascending (lowest first — needs attention)
    return entries.sort((a, b) => (a[1].balance ?? -1) - (b[1].balance ?? -1));
  }, [balances.otcBalances]);

  const hasOtc = otcEntries.length > 0;
  const hasKiosks = balances.kioskBalances.length > 0;

  // Compute shop-level totals
  const otcTotal = otcEntries.reduce((sum, [, t]) => sum + (t.balance ?? 0), 0);
  const kioskTotal = balances.kioskBalances.reduce((sum, k) => sum + (k.balance?.balance ?? 0), 0);
  const shopTotal = otcTotal + kioskTotal;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold">{balances.shopName}</h2>
          <p className="text-sm text-muted-foreground">
            Actualizado: {new Date(balances.fetchedAt).toLocaleString('es-PR')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onBack}>
          &larr; Resumen
        </Button>
      </div>

      {/* Shop total metric */}
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <p className="text-sm text-muted-foreground">Total en esta localidad</p>
        <p className="mt-1 font-display text-4xl font-bold">{formatCurrency(shopTotal)}</p>
        <div className="mt-2 flex justify-center gap-6 text-sm">
          <span>
            OTC: <span className="font-semibold text-emerald-600">{formatCurrency(otcTotal)}</span>
          </span>
          {hasKiosks ? (
            <span>
              Kioscos:{' '}
              <span className="font-semibold text-blue-600">{formatCurrency(kioskTotal)}</span>
            </span>
          ) : null}
        </div>
      </div>

      {/* OTC Terminals */}
      {hasOtc ? (
        <section className="space-y-4">
          <h3 className="font-display text-lg font-bold">
            Terminales OTC ({String(otcEntries.length)})
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {otcEntries.map(([terminal, info]) => (
              <div
                key={terminal}
                className={`rounded-xl border p-5 shadow-raised ${getHealthBg(info.balance, info.lastDeposit)}`}
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm font-semibold">{terminal}</p>
                  <p
                    className={`font-display text-2xl font-bold ${getHealthColor(info.balance, info.lastDeposit)}`}
                  >
                    {info.balance !== null ? formatCurrency(info.balance, info.currency) : '—'}
                  </p>
                </div>

                {info.balance !== null && info.lastDeposit !== null ? (
                  <ProgressBar current={info.balance} max={info.lastDeposit} />
                ) : null}

                {info.balance !== null ? (
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {info.lastDeposit !== null ? (
                      <p>Deposito: {formatCurrency(info.lastDeposit)}</p>
                    ) : null}
                    {info.lastDepositTime !== null ? (
                      <p>{info.lastDepositTime.slice(0, 10)}</p>
                    ) : null}
                    {info.betsReceived > 0 ? (
                      <p className="text-emerald-700">
                        + Apuestas: {formatCurrency(info.betsReceived)}
                      </p>
                    ) : null}
                    {info.additionalDeposits > 0 ? (
                      <p className="text-emerald-700">
                        + Depositos: {formatCurrency(info.additionalDeposits)}
                      </p>
                    ) : null}
                    {info.payouts > 0 ? (
                      <p className="text-red-600">- Pagos: {formatCurrency(info.payouts)}</p>
                    ) : null}
                    {info.cashDrops > 0 ? (
                      <p className="text-red-600">- Drops: {formatCurrency(info.cashDrops)}</p>
                    ) : null}
                    {info.withdrawals > 0 ? (
                      <p className="text-red-600">- Retiros: {formatCurrency(info.withdrawals)}</p>
                    ) : null}
                    {info.vouchersPaid > 0 ? (
                      <p className="text-red-600">
                        - Vouchers: {formatCurrency(info.vouchersPaid)}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">{info.note}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : (
        <p className="text-muted-foreground">Sin datos de OTC para esta localidad.</p>
      )}

      {/* Kiosk Balances */}
      {hasKiosks ? (
        <section className="space-y-4">
          <h3 className="font-display text-lg font-bold">
            Kioscos SST ({String(balances.kioskBalances.length)})
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {balances.kioskBalances.map((kiosk) => (
              <div
                key={kiosk.terminalRef}
                className="rounded-xl border border-border bg-card p-5 shadow-raised"
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm font-semibold">{kiosk.name}</p>
                  {kiosk.balance !== null ? (
                    <p className="font-display text-2xl font-bold text-blue-600">
                      {formatCurrency(kiosk.balance.balance, kiosk.balance.currency)}
                    </p>
                  ) : null}
                </div>
                {kiosk.error !== null ? (
                  <p className="mt-2 text-sm text-destructive">{kiosk.error}</p>
                ) : kiosk.balance !== null ? (
                  <>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(kiosk.balance.timestamp).toLocaleString('es-PR')}
                    </p>
                    {kiosk.balance.denominations.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {[...kiosk.balance.denominations]
                          .sort((a, b) => b.denomination - a.denomination)
                          .map((d) => (
                            <span
                              key={d.denomination}
                              className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium"
                            >
                              {String(d.count)}&times;${String(d.denomination)}
                            </span>
                          ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">Sin datos</p>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

// ─── Page Component ──────────────────────────────────────────────────────────

export const BalancesPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const csrfToken = authenticated?.csrfToken ?? '';
  const api = useMemo(() => createBalancesApi(session.client), [session.client]);

  const [state, setState] = useState<PageState>({ status: 'checking' });

  const loadSummary = useCallback(() => {
    setState({ status: 'loading-summary' });
    void api.getSummary().then(
      (summary) => setState({ status: 'summary', summary }),
      () => setState({ status: 'unauthenticated' }),
    );
  }, [api]);

  // Check for existing Kambi session on mount
  useEffect(() => {
    void api.getSession().then(
      (sessionStatus: KambiSessionStatus) => {
        if (sessionStatus.active) {
          loadSummary();
        } else {
          setState({ status: 'unauthenticated' });
        }
      },
      () => setState({ status: 'unauthenticated' }),
    );
  }, [api, loadSummary]);

  const handleAuthSuccess = useCallback(() => {
    loadSummary();
  }, [loadSummary]);

  const handleSelectShop = useCallback(
    (shopRef: string) => {
      if (state.status !== 'summary' && state.status !== 'shop-detail') return;
      const summary = state.summary;
      setState({ status: 'loading-shop', summary, shopRef });
      void api.getShopBalances(shopRef).then(
        (balances) => setState({ status: 'shop-detail', summary, balances }),
        () => setState({ status: 'summary', summary }),
      );
    },
    [api, state],
  );

  const handleBack = useCallback(() => {
    if (state.status === 'shop-detail') {
      setState({ status: 'summary', summary: state.summary });
    }
  }, [state]);

  if (authenticated === null) return <></>;

  return (
    <section aria-labelledby="balances-heading" className="space-y-8">
      <div>
        <h1 id="balances-heading" className="font-display text-2xl font-bold">
          Balances OTC y Kioscos
        </h1>
        <p className="text-muted-foreground">
          Balance en tiempo real de las cajas OTC y kioscos de autoservicio.
        </p>
      </div>

      {state.status === 'checking' || state.status === 'loading-summary' ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
          {state.status === 'loading-summary' ? (
            <p className="text-sm text-muted-foreground">
              Cargando balances de todas las localidades...
            </p>
          ) : null}
        </div>
      ) : null}

      {state.status === 'unauthenticated' ? (
        <KambiLoginForm api={api} csrfToken={csrfToken} onSuccess={handleAuthSuccess} />
      ) : null}

      {state.status === 'summary' ? (
        <div className="space-y-8">
          <GrandTotalHero summary={state.summary} />
          <ShopSummaryGrid shops={state.summary.shops} onSelect={handleSelectShop} />
        </div>
      ) : null}

      {state.status === 'loading-shop' ? (
        <div className="space-y-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setState({ status: 'summary', summary: state.summary })}
          >
            &larr; Resumen
          </Button>
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
          </div>
        </div>
      ) : null}

      {state.status === 'shop-detail' ? (
        <ShopDetailView balances={state.balances} onBack={handleBack} />
      ) : null}
    </section>
  );
};
